/**
 * Agent Session Service (BL-1bb921de)
 * AIDLC Administrative Backend
 *
 * Read-only calls for agent session timestamps and time active.
 */

import apiClient from './api';

export interface AgentSession {
  id: string;
  agentId: string;
  task: string | null;
  itemId: string | null;
  itemUnknown: boolean;
  unlinked: boolean;
  featureId: string | null;
  sprintId: string | null;
  source: 'report' | 'dispatcher' | 'relay';
  startedAt: string;
  endedAt: string | null;
  activeMs: number;
  waitingMs: number;
  endReason: string | null;
  /** BL-8550fb1c: the cost of the token records linked to the session. */
  costUsd: number;
  costRecords: number;
  costSource: 'exact' | 'estimated' | 'mixed' | null;
  /** It ended without reporting what it cost. */
  costMissing: boolean;
  /** The orchestrator is still to report its cost. */
  costPending: boolean;
}

export interface SessionSummaryRow {
  key: string | null;
  sessions: number;
  activeMs: number;
  waitingMs: number;
  unlinkedActiveMs: number;
  /** Wall-clock time with at least one session working: parallel runs counted once. */
  busyMs: number;
  unverifiedMs: number;
  /** BL-8550fb1c: the cost of these sessions' token records in the window. */
  costUsd: number;
  costMissingSessions: number;
  costPendingSessions: number;
  /** Some of costUsd was priced from token counts. */
  costEstimated: boolean;
  openSessions: AgentSession[];
}

export interface ItemTimeActive {
  itemId: string;
  sessions: number;
  activeMs: number;
  waitingMs: number;
  busyMs: number;
  costUsd: number;
  costMissingSessions: number;
  costPendingSessions: number;
  costEstimated: boolean;
  byAgent: SessionSummaryRow[];
}

/** Today's (America/Detroit) time per agent. */
export async function fetchTodaySummaryByAgent(): Promise<SessionSummaryRow[]> {
  const response = await apiClient.get<{ rows: SessionSummaryRow[] }>('/agent-sessions/summary?groupBy=agent');
  return response.data.rows;
}

export async function fetchItemTimeActive(itemId: string): Promise<ItemTimeActive> {
  const response = await apiClient.get<ItemTimeActive>(`/backlog/${encodeURIComponent(itemId)}/sessions`);
  return response.data;
}

/** "$1.84", "<$0.01" for a cost too small to show in cents, "$0.00" for none. */
export function formatCostUsd(usd: number | undefined): string {
  const value = typeof usd === 'number' && Number.isFinite(usd) ? usd : 0;
  if (value > 0 && value < 0.01) return '<$0.01';
  return `$${value.toFixed(2)}`;
}

/** " (2 without cost, 1 cost pending)": the sessions whose cost is not in the figure, or '' when none. */
export function missingCostNote(missing: number | undefined, pending?: number): string {
  const parts = [missing ? `${missing} without cost` : '', pending ? `${pending} cost pending` : ''].filter(Boolean);
  return parts.length ? ` (${parts.join(', ')})` : '';
}

const MS_PER_MINUTE = 60000;
const MINUTES_PER_HOUR = 60;

/** "Xh Ym", "Ym" under an hour, "<1m" under a minute. */
export function formatDurationMs(ms: number): string {
  if (ms > 0 && ms < MS_PER_MINUTE) return '<1m';
  const minutes = Math.max(0, Math.floor(ms / MS_PER_MINUTE));
  const hours = Math.floor(minutes / MINUTES_PER_HOUR);
  return hours > 0 ? `${hours}h ${minutes % MINUTES_PER_HOUR}m` : `${minutes}m`;
}
